/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * angular import
 */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

/**
 * RxJS imports
 */
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * service import
 */
import { FunCollectionService } from '../../common/common-functions/fun-collection.service';
import { JwtDecodeService } from '../../dataservices/jwt-decode.service';
import { PostApiService } from '../../dataservices/post-api.service';
import { MasterserviceService } from '../../../app/dataservices/master-service/masterservice.service';
import { NewLanguageService } from '../../../app/dataservices/new-language/new-language.service';
import { LocalstorageService } from '../../auth/localstorage.service';

/**
 * constant import
 */
import { NUMBERS, PRODUCT_TYPE_CONSTANT, NCP_SUB_PRODUCT_CARD_TYPE } from '../../constants/app.constants';

/**
 * model import
 */
import { LikeObject, NcpProduct, Channel } from '../model/ncp.model';
import { AnalyticsInterface } from '../../analytics-event/analytics.model';
import { EVENT_NAME } from '../../analytics-event/analytics.constant';

@Component({
  selector: 'app-game-card',
  templateUrl: './game-card.component.html',
  styleUrls: ['./game-card.component.scss']
})
export class GameCardComponent implements OnInit, OnDestroy {
  /**
   * @cardType defines view of game card to be rendered
   * @callingFrom defines which screen is using the card
   * @product defins a single product
   * @channel defins a channel for product
   */
  @Input() cardType: 'single' | 'multi';
  @Input() callingFrom: string;
  @Input() product: NcpProduct;
  @Input() imgHeight: number;
  @Input() channel?: Channel;

  /**
   * @loader defines a ongoing event
   */
  loader = false;

  /**
   * @notifier$ defines a notifier subject stream to end data streaming
   */
  private notifier$ = new Subject();

  /**
   * @likeObject defines a object for like Object
   * @showLikeListModal defines a like list modal
   * @productType defines product type constant
   * @number defines Numbers const
   * @cardTypeConst defines Card Type constant
   */
  public likeObject: LikeObject;
  public showLikeListModal: boolean;
  public productType = PRODUCT_TYPE_CONSTANT;
  public numbers = NUMBERS;
  public cardTypeConst = NCP_SUB_PRODUCT_CARD_TYPE;
  public analyticsGameData: AnalyticsInterface;

  constructor(
    public mls: NewLanguageService,
    private masterService: MasterserviceService,
    private jwtService: JwtDecodeService,
    private fnCollectionService: FunCollectionService,
    private postService: PostApiService,
    private router: Router,
    private lcs: LocalstorageService) { }

  ngOnInit(): void {
    this.generateAnalyticsData();
  }

  /**
   * getter for specialities
   */
  get specialities(): any {
    return this.product.specialities;
  }

  /**
   * getter for sponser
   */
  get sponser(): any {
    return this.product.sponser_details;
  }

  /**
   * getter for template color
   */
  get templateColor(): any {
    return this.product.template.color;
  }

  /**
   * getter for template image
   */
  get templateImage(): any {
    return this.product.template.image;
  }

  /**
   * getter for template text
   */
  get templateText(): any {
    return this.product.template.text;
  }

  /**
   * getter for count
   */
  get count(): any {
    return this.product.count;
  }

  /**
   * getter for image
   */
  get image(): any {
    return this.product.image;
  }

  /**
   * @EventHandler navigate to product specific screen
   */
  navigateToGameScreen(): void {
    this.updatePlayCount();
  }

  /**
   * @EventHandler navigate to product detail page
   */
  navigateToGameDetail(isBannerRedirect?: boolean): void {
    if (!this.product.banner_redirection) {
      this.router.navigate([`/game/detail/${this.product.product_type_id}`]);
    } else {
      this.updatePlayCount(isBannerRedirect);
    }
  }

  /**
   * @EventHandler to like product
   */
  likeProduct(): boolean {
    if (this.masterService.isOnline == false) {
      this.masterService.isConnectionModalShown.next('1');
      return false;
    }

    if (this.loader === true) {
      return;
    }
    if (this.product.is_liked === NUMBERS.ONE) {
      return;
    }
    this.loader = true;
    this.postService.productLike(this.product.product_type,
      this.product.product_type_id, 'like').
      pipe(
        takeUntil(this.notifier$)
      ).subscribe(response => {
        this.loader = false;
        this.jwtService.decodeData(response).then(data => {
          if (data['status'] === NUMBERS.ONE) {
            this.fnCollectionService.showSuccess(data['msg'], 'Success');
            this.product.is_liked = 1;
            this.product.count.like = this.product.count.like + 1;
          } else if (data['status'] === NUMBERS.ZERO) {
            this.fnCollectionService.errorHandeler(data);
            return;
          }
        }, () => {
          this.loader = false;
        });
      });
  }

  /**
   * @EventHandler to share product
   */
  shareProduct(): boolean {
    if (this.masterService.isOnline == false) {
      this.masterService.isConnectionModalShown.next('1');
      return false;
    }

    const share = [{
      share_url: '',
      share_content: '',
      product_type: this.product.product_type,
      product_type_id: this.product.product_type_id
    }];
    this.fnCollectionService.productShare(share);
  }

  /**
   * @EventHandler to get product Like List
   */
  getProductLikeList(): boolean {
    if (this.masterService.isOnline == false) {
      this.masterService.isConnectionModalShown.next('1');
      return false;
    }

    this.likeObject = {
      'product_id': this.product.product_type_id,
      'product_type': this.product.product_type,
      'callLikeProperty': 'list'
    };
    this.showLikeListModal = true;
    return true;
  }

  /**
   * @EventHandler reset Like
   */
  resetLike(): void {
    this.loader = false;
    this.showLikeListModal = false;
  }

  /**
   * @EventHandler to clean up memory/Event handler/subscription
   */
  ngOnDestroy(): void {
    this.notifier$.next();
    this.notifier$.complete();
  }

  /**
   * @EventHandler to capture play count
   */
  updatePlayCount(isBannerRedirect?: boolean): void {
    this.postService.updatePlayCount(this.product.product_type_id).
      pipe(takeUntil(this.notifier$)).
      subscribe(response => {
        this.jwtService.decodeData(response).then(data => {
          if (data['status'] === NUMBERS.ONE) {
            if (isBannerRedirect) {
              this.lcs.setInLocalstorage('isBannerRedirect', true);
              this.fnCollectionService.validateTargetType(this.product.banner_redirection, this.product.product_type_id);
            } else {
              this.lcs.setInLocalstorage('isBannerRedirect', false);
              this.fnCollectionService.validateTargetType(this.product.target, this.product.product_type_id);
            }
          } else if (data['status'] === NUMBERS.ZERO) {
            // No Code
          }
        }, error => {
          console.log(error);
        });
      });
  }
  /**
   * getter for Like color
   */
  get likeColor(): string {
    return this.product.is_liked === NUMBERS.ONE ? this.templateColor.liked_color : this.templateColor.like_comment_share;
  }

  /**
   * getter for specialty count
   */
  get specialtyCount(): string {
    return this.specialities.length > NUMBERS.ONE ? ` +${this.specialities.length - 1}` : '';
  }

  /**
   * getter for specialty name
   */
  get specialtyName(): string {
    if (this.specialities.length) {
      return this.specialities[0].speciality_name;
    }
  }
  /**
   * getter for specialty title
   */
  get specialtyTitle(): string {
    let title = '';
    this.specialities.map((specialty) => {
      title += `${specialty.speciality_name},`;
    });
    return title.slice(0, -1);
  }

  generateAnalyticsData(): void {
    const events = {
      'analyticsPlayNowData': EVENT_NAME.GET_STARTED_CLICK,
      'analyticsLikeData': EVENT_NAME.NCP_LIKE_CLICK,
      'analyticsCommentData': EVENT_NAME.NCP_CARD_COMMENT_CLICK,
      'analyticsShareData': EVENT_NAME.NCP_SHARE_CLICK,
      'analyticsGameData': EVENT_NAME.NCP_CARD_CLICK
    };
    // tslint:disable-next-line: forin
    for (const event in events) {
      this[event] = {
        'event_name': events[event],
        'product_type': PRODUCT_TYPE_CONSTANT.NCP_SUB_TYPE,
        'product_type_id': this.product.product_type_id,
        'screen_name': this.callingFrom,
      };
    }
  }
  /**
   * navigate to product detail page
   */
  navigateToProductDetail(): void {
    this.router.navigate([`/game/detail/${this.product.product_type_id}`]);
  }
}
